/// <amd-module name="Test/Analytics/Common/Definitions" />
import { ConfigurationProps, PeriodConfigurationProps } from "Analytics/Shared/Common/Definitions";
import * as Resources from "Test/Analytics/Common/Resources";
import * as CommonTypes from "Test/Analytics/Common/Types";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { FilterValueItem } from "Test/common-util/TestResults.Filtering.Common";
import { format } from "VSS/Core/Util/String";

export class TrendChartOptions {
    public static readonly width: number = 1000; // Pixels
    public static readonly height: number = 360; // Pixels

    public static readonly maxStackedSeriesCount: number = 10;
}

export class ReportConfigurationDefinitions {
    public get defaultChartMetricValue(): CommonTypes.Metric {
        return CommonTypes.Metric.ResultCountAndPassRate;
    }

    public get defaultDetailedTestListSortedColumn(): CommonTypes.IDetailedListColumn {
        return {
            column: CommonTypes.ColumnIndices.FailedCount,
            sortOrder: CommonTypes.SortOrder.Descending
        };
    }

    private _outcomes: OutcomeConfigurationProps;
    private _groupBy: GroupByConfigurationProps;
    private _period: PeriodConfigurationProps;
    private _chartMetrics: ChartMetricProps;
    private _testInsightsChartMetrics: TestInsightsChartMetricProps;
    constructor() {
        this._outcomes = new OutcomeConfigurationProps();
        this._groupBy = new GroupByConfigurationProps();
        this._period = new PeriodConfigurationProps(CommonTypes.Period.Days_30);
        this._chartMetrics = new ChartMetricProps();
        this._testInsightsChartMetrics = new TestInsightsChartMetricProps();
    }

    public getDefaultConfigurationValues(contextType: TCMContracts.TestResultsContextType): CommonTypes.IReportConfiguration {
        return {
            groupBy: CommonTypes.GroupBy.None,
            trendBy: CommonTypes.TrendBy.Days,
            period: CommonTypes.Period.Days_14,
            outcomes: [CommonTypes.TestOutcome.Failed],
            configuredFilters:
                contextType === TCMContracts.TestResultsContextType.Build
                    ? {
                          [CommonTypes.Filter.Workflow]: {
                              values: [
                                  new FilterValueItem(
                                      CommonTypes.Workflow.Build.toString(),
                                      new WorkflowConfigurationProps().options[CommonTypes.Workflow.Build]
                                  )
                              ]
                          }
                      }
                    : {}
        };
    }

    public getOutcomeConfigurationProps(): CommonTypes.IConfigurationProps {
        return this._outcomes;
    }

    public getGroupByConfigurationProps(): CommonTypes.IConfigurationProps {
        return this._groupBy;
    }

    public getPeriodConfigurationProps(): CommonTypes.IConfigurationProps {
        return this._period;
    }

    public getChartMetricsProps(): CommonTypes.IConfigurationProps {
        return this._chartMetrics;
    }

    public getTestInsightsChartMetricProps(): CommonTypes.IConfigurationProps {
        return this._testInsightsChartMetrics;
    }
}

export class WorkflowConfigurationProps extends ConfigurationProps {
    protected _initializeOptions() {
        this._options[CommonTypes.Workflow.Build] = Resources.BuildLabel;
        this._options[CommonTypes.Workflow.Release] = Resources.ReleaseText;
    }
}

export class OutcomeConfigurationProps extends ConfigurationProps {
    protected _initializeOptions() {
        this._options[CommonTypes.TestOutcome.Passed] = Resources.TestOutcome_Passed;
        this._options[CommonTypes.TestOutcome.Failed] = Resources.TestOutcome_Failed;
        this._options[CommonTypes.TestOutcome.Inconclusive] = Resources.TestOutcome_Inconclusive;
        this._options[CommonTypes.TestOutcome.Aborted] = Resources.TestOutcome_Aborted;
        this._options[CommonTypes.TestOutcome.NotExecuted] = Resources.TestOutcome_NotExecuted;
        this._options[CommonTypes.TestOutcome.Error] = Resources.TestOutcome_Error;
        this._options[CommonTypes.TestOutcome.NotImpacted] = Resources.TestOutcome_NotImpacted;
    }
}

export class GroupByConfigurationProps extends ConfigurationProps {
    protected _initializeOptions() {
        this._options[CommonTypes.GroupBy.None] = Resources.DisplayTextNone;
        this._options[CommonTypes.GroupBy.Container] = Resources.TestFileText;
        this._options[CommonTypes.GroupBy.Owner] = Resources.FilterByOwner;
        this._options[CommonTypes.GroupBy.Priority] = Resources.PriorityText;
        this._options[CommonTypes.GroupBy.Environment] = Resources.Stage;
        this._options[CommonTypes.GroupBy.Branch] = Resources.BranchText;
        this._options[CommonTypes.GroupBy.TestRun] = Resources.TestRunText;
    }
}

export class ChartMetricProps extends ConfigurationProps {
    protected _initializeOptions() {
        this._options[CommonTypes.Metric.ResultCountAndPassRate] = Resources.ResultCountAndPassRateText;
        this._options[CommonTypes.Metric.ResultCount] = Resources.ResultCountText;
        this._options[CommonTypes.Metric.AvgDuration] = Resources.AverageDurationText;
    }
}

export class TestInsightsChartMetricProps extends ConfigurationProps {
    protected _initializeOptions() {
        this._options[CommonTypes.Metric.ResultCount] = Resources.ResultCountText;
    }
}
